import { useState } from 'react'
import { UpdateMeetingAgendaRequest } from '../../generated'

export interface MeetingAgendaSectionProps {
  wordOfTheDay?: string
  theme?: string
  // Authenticated club members may edit; anonymous/non-members get read-only.
  canEdit: boolean
  isSubmitting: boolean
  // Throws on failure so this component can keep the editor open while the
  // parent surfaces the error; resolves on success to drop back to read mode.
  onSave: (body: UpdateMeetingAgendaRequest) => Promise<void>
}

type EditableField = 'theme' | 'wordOfTheDay'

const MAX_LENGTH = 255

const buttonClassName =
  'justify-center inline-flex items-center gap-x-1.5 rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60'

const MeetingAgendaSection = ({
  wordOfTheDay,
  theme,
  canEdit,
  isSubmitting,
  onSave,
}: MeetingAgendaSectionProps) => {
  // Only one field is edited at a time, mirroring the per-row edit on the
  // participants list. null means nothing is in edit mode.
  const [editingField, setEditingField] = useState<EditableField | null>(null)
  const [draft, setDraft] = useState('')

  const values: Record<EditableField, string | undefined> = {
    theme,
    wordOfTheDay,
  }

  // Members always get a row per field so they can add or edit each one
  // independently; non-members only see fields that are set.
  const showTheme = canEdit || Boolean(theme)
  const showWord = canEdit || Boolean(wordOfTheDay)

  // Nothing to show and read-only → stay invisible so the page looks exactly as
  // it did before this feature.
  if (!showTheme && !showWord) {
    return null
  }

  const startEditing = (field: EditableField) => {
    setDraft(values[field] ?? '')
    setEditingField(field)
  }

  const handleSave = async (field: EditableField) => {
    try {
      // Only the edited field is sent; a blank value clears it, the other field
      // is left untouched by the backend.
      await onSave({ [field]: draft.trim() })
      setEditingField(null)
    } catch {
      // Parent surfaces the error; keep the editor open so the user can retry.
    }
  }

  const renderRow = (field: EditableField, label: string) => {
    const value = values[field]
    const isEditing = editingField === field

    if (isEditing) {
      return (
        <div className="grid grid-cols-3 gap-4 items-center min-h-8 max-w-xl">
          <div className="col-span-2">
            <input
              id={`agenda-${field}`}
              name={field}
              type="text"
              maxLength={MAX_LENGTH}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={label}
              autoFocus
              className="block w-full rounded-md border-0 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600"
            />
          </div>
          <div className="flex items-center gap-x-2">
            <button
              type="button"
              onClick={() => handleSave(field)}
              disabled={isSubmitting}
              className="justify-center inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60">
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setEditingField(null)}
              disabled={isSubmitting}
              className={buttonClassName}>
              Cancel
            </button>
          </div>
        </div>
      )
    }

    return (
      <div className="grid grid-cols-3 gap-4 items-center min-h-8 max-w-xl">
        <p className="block text-sm text-gray-900 col-span-2 break-words">
          <span className="font-bold">{label}:</span>{' '}
          {value ? (
            value
          ) : (
            <span className="italic text-gray-400">Not set</span>
          )}
        </p>
        {canEdit && (
          <div className="flex items-center gap-x-2">
            <button
              type="button"
              onClick={() => startEditing(field)}
              className={buttonClassName}>
              {value ? 'Edit' : 'Add'}
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="py-6">
      <dt className="text-base font-semibold leading-6 text-gray-900">
        Meeting Agenda
      </dt>
      <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
        The theme and word of the day for this meeting
      </dd>
      <div className="mt-6 space-y-3 max-w-xl">
        {showTheme && renderRow('theme', 'Theme')}
        {showWord && renderRow('wordOfTheDay', 'Word of the Day')}
      </div>
    </div>
  )
}

export default MeetingAgendaSection
